// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useAnomalies } from './useAnomalies';
import { useConsoleAlarms } from './useConsoleAlarms';
import { useSiteApprovals } from './useSiteApprovals';
import { useActiveSite } from './useActiveSite';
import { usePersona } from './usePersona';
import { countImminentApproved, decidedPlans, pendingReviewPlans } from '../services/planSelectors';
import { useSiteTime } from './useSiteTime';
import type { AlarmTabKey } from '../services/alarmSelectors';

const EMPTY_COUNTS: Record<AlarmTabKey, number | null> = {
  alarms: null,
  anomalies: null,
  routine: null,
  recommendations: null,
  closed: null,
};

/**
 * Count badges for the Home top-nav (`AlarmTabs`), shared by every Home screen
 * so the badges are identical wherever the bar renders.
 *
 * Base counts come from the operator-console alarm set, but two badges are
 * overridden because the console's own count doesn't match what the tab lists:
 *
 * - **Anomalies**: served by the real API, not the client-side alarm split.
 *   Capped at 99+ in `AlarmTabs`.
 * - **Recommendations**: always plan-derived — the console's count for that tab
 *   is `optwork` alarms, unrelated to the plans the tab lists. Ops Managers see
 *   plans awaiting their approval that execute today or later; everyone else
 *   sees approved plans executing today or tomorrow, the same set the screen
 *   renders (see `imminentPlans`).
 * - **Closed**: the mixed queue's "All" — the console's closed rows plus closed
 *   findings and decided plans (rejected ones for the Ops Manager only), the
 *   same union `ClosedQueue` lists.
 *
 * Zero maps to `null` so no badge shows.
 */
export function useHomeTabCounts(): Record<AlarmTabKey, number | null> {
  const { siteId } = useActiveSite();
  const { persona } = usePersona();
  const { data } = useConsoleAlarms(siteId);
  const { count: anomalies, items: anomalyItems = [] } = useAnomalies(siteId);
  const { approvals } = useSiteApprovals();
  const { siteZone } = useSiteTime();

  const base = data?.counts ?? EMPTY_COUNTS;
  const recommendations =
    persona === 'manager'
      ? pendingReviewPlans(approvals, undefined, siteZone).length
      : countImminentApproved(approvals, undefined, siteZone);

  const closed =
    (base.closed ?? 0) +
    anomalyItems.filter(a => a.closed).length +
    decidedPlans(approvals, { includeRejected: persona === 'manager' }).length;

  return {
    ...base,
    anomalies: anomalies || null,
    recommendations: recommendations || null,
    closed: closed || null,
  };
}
